import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import dayjs from "dayjs";

import { useTruckList, useTruckListDispatchers } from "@/hooks/useTruckList";
import { Pagination } from "@/components/common/pagination/Pagination";
import { TruckListHeader, TruckListSelect, TruckListTable, TruckListToolbar } from "@/components/trucklist";

// Equipment item `id`s are the `TrailerType` enum values from openapi/v0.yaml —
// the page sends them straight through as the `equipment` query param.
const EQUIPMENT_ITEMS = [
    { id: "all", text: "All equipment" },
    { id: "dry_van", text: "Van" },
    { id: "reefer", text: "Reefer" },
    { id: "flatbed", text: "Flatbed" },
];

const PER_PAGE_ITEMS = [
    { id: 20, text: "20" },
    { id: 48, text: "48" },
    { id: 100, text: "100" },
];

const DEFAULT_PER_PAGE = 20;
const DEFAULT_SORT_KEY = "age";
const DEFAULT_SORT_DIRECTION = "asc";
// While a location is searched the default order is closest first. The Location column sorts by
// distance then, so a leftover distance sort without a search falls back to sorting by location.
const DISTANCE_SORT_KEY = "distance";
// Idle time (ms) after the last keystroke before the location search is pushed to the URL
// and refetched against the backend. Tuned to feel responsive without firing a request per key.
const LOCATION_DEBOUNCE_MS = 400;

// Sets a query param, or removes it when the value equals its default / is empty,
// so default state never clutters the URL.
const setParam = (params, key, value, defaultValue) => {
    if (value == null || value === "" || value === defaultValue) params.delete(key);
    else params.set(key, value);
};

// Builds a structured value the date picker understands from the three ready-date URL
// params. `readyDate=today` is the only preset; explicit dates / ranges live in
// `readyDateStart` + `readyDateEnd`, with a same-day pair encoding a single date.
const parseReadyDate = (searchParams) => {
    if (searchParams.get("readyDate") === "today") return { kind: "today" };
    const start = searchParams.get("readyDateStart");
    const end = searchParams.get("readyDateEnd");
    if (start && end) {
        if (start === end) return { kind: "single", start, end };
        return { kind: "range", start, end };
    }
    return { kind: "all" };
};

// Inclusive [from, to] window the API expects.
const readyDateRange = (value) => {
    if (value.kind === "all") return { from: undefined, to: undefined };
    if (value.kind === "today") {
        const today = dayjs().format("YYYY-MM-DD");
        return { from: today, to: today };
    }
    return { from: value.start, to: value.end };
};

// Public, slug-based page listing a carrier's available trucks. Filter / sort /
// pagination state lives in the URL query string so the current view is shareable
// via link and survives a page reload; the URL drives the API request, so the
// backend handles all filtering, sorting and pagination.
export const TruckList = () => {
    const { slug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const urlLocation = searchParams.get("location") ?? "";
    const searching = urlLocation.trim() !== "";
    const equipment = searchParams.get("equipment") ?? "all";
    const readyDate = useMemo(() => parseReadyDate(searchParams), [searchParams]);
    const dispatcher = searchParams.get("dispatcher") ?? "all";
    const defaultSortKey = searching ? DISTANCE_SORT_KEY : DEFAULT_SORT_KEY;
    const sort = useMemo(() => {
        const key = searchParams.get("sort") ?? defaultSortKey;
        return {
            key: key === DISTANCE_SORT_KEY && !searching ? "location" : key,
            direction: searchParams.get("dir") ?? DEFAULT_SORT_DIRECTION,
        };
    }, [searchParams, searching, defaultSortKey]);
    const page = Math.max(1, Number(searchParams.get("page")) || 1);
    const perPage = Number(searchParams.get("perPage")) || DEFAULT_PER_PAGE;

    const { from: readyDateFrom, to: readyDateTo } = useMemo(() => readyDateRange(readyDate), [readyDate]);

    const listParams = useMemo(
        () => ({
            location: urlLocation.trim() || undefined,
            dispatcher,
            equipment,
            readyDateFrom,
            readyDateTo,
            sortKey: sort.key,
            sortDirection: sort.direction,
            page,
            pageSize: perPage,
        }),
        [urlLocation, dispatcher, equipment, readyDateFrom, readyDateTo, sort, page, perPage],
    );

    const { data, isLoading, isError } = useTruckList(slug, listParams);
    const { data: dispatchersData } = useTruckListDispatchers(slug);

    const trucks = data?.trucks ?? [];
    const totalCount = data?.totalCount ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
    const safePage = Math.min(page, totalPages);

    // Applies a set of query-param changes in a single history-replacing update.
    const updateParams = useCallback(
        (mutate) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    mutate(next);
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    // Local mirror of the location input. We keep typing snappy by holding the raw value
    // in component state, and only push it to the URL (and therefore to the API request)
    // after `LOCATION_DEBOUNCE_MS` of quiet — avoids a backend hit per keystroke.
    const [locationInput, setLocationInput] = useState(urlLocation);

    // Re-sync the input when the URL is changed from elsewhere (e.g. Clear all, or the
    // user navigating back). Without this the input would lag the URL after a clear.
    useEffect(() => {
        setLocationInput(urlLocation);
    }, [urlLocation]);

    // Debounced URL write. `replace: true` (inside `updateParams`) keeps history clean so
    // each keystroke doesn't add a back-stack entry. The early-return guards against a
    // redundant write when the input was just synced from the URL. Starting or ending a search
    // drops the chosen sort, so a new search opens on the closest trucks.
    useEffect(() => {
        if (locationInput === urlLocation) return;
        const timeoutId = setTimeout(() => {
            updateParams((params) => {
                if ((locationInput.trim() !== "") !== searching) {
                    params.delete("sort");
                    params.delete("dir");
                }
                setParam(params, "location", locationInput, "");
                params.delete("page");
            });
        }, LOCATION_DEBOUNCE_MS);
        return () => clearTimeout(timeoutId);
    }, [locationInput, urlLocation, searching, updateParams]);

    const setEquipment = (value) =>
        updateParams((params) => {
            setParam(params, "equipment", value, "all");
            params.delete("page");
        });

    // Maps the picker's `{ kind, start?, end? }` shape onto the three URL params.
    // Only one of `readyDate=today` and `readyDateStart`/`readyDateEnd` is ever set —
    // the other slot is cleared so a stale param can't outlive the new selection.
    const setReadyDate = (value) =>
        updateParams((params) => {
            params.delete("readyDate");
            params.delete("readyDateStart");
            params.delete("readyDateEnd");
            if (value.kind === "today") {
                params.set("readyDate", "today");
            } else if (value.kind === "single" || value.kind === "range") {
                params.set("readyDateStart", value.start);
                params.set("readyDateEnd", value.end);
            }
            params.delete("page");
        });

    const setDispatcher = (value) =>
        updateParams((params) => {
            setParam(params, "dispatcher", value, "all");
            params.delete("page");
        });

    // A first click on distance sorts closest first rather than the table's usual descending.
    const setSort = (key, direction) =>
        updateParams((params) => {
            setParam(params, "sort", key, defaultSortKey);
            setParam(params, "dir", key === DISTANCE_SORT_KEY && sort.key !== key ? "asc" : direction, DEFAULT_SORT_DIRECTION);
            params.delete("page");
        });

    const setPerPage = (value) =>
        updateParams((params) => {
            setParam(params, "perPage", String(value), String(DEFAULT_PER_PAGE));
            params.delete("page");
        });

    const setPage = (value) => updateParams((params) => setParam(params, "page", String(value), "1"));

    const handleClearAll = () =>
        updateParams((params) => {
            ["location", "equipment", "dispatcher", "readyDate", "readyDateStart", "readyDateEnd", "page"].forEach((key) =>
                params.delete(key),
            );
            if (searching) {
                params.delete("sort");
                params.delete("dir");
            }
        });

    // Dispatcher options come from the dedicated endpoint so they stay stable
    const dispatcherItems = useMemo(
        () => [
            { id: "all", text: "All dispatchers" },
            ...(dispatchersData ?? []).filter((entry) => entry.name?.trim()).map((entry) => ({ id: entry.id, text: entry.name })),
        ],
        [dispatchersData],
    );

    // Once data has loaded, drop an out-of-range page from the URL.
    useEffect(() => {
        if (!isLoading && !isError && page > totalPages) setPage(totalPages);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, isError, page, totalPages]);

    return (
        <div className="w-full bg-cm-p-background pb-16">
            <TruckListHeader name={data?.carrier?.name} />

            <main className="mx-auto max-w-[1472px] px-4 pt-8 sm:px-6">
                <TruckListTable
                    toolbar={
                        <TruckListToolbar
                            count={totalCount}
                            loading={isLoading}
                            locationQuery={locationInput}
                            onLocationChange={setLocationInput}
                            searchLocation={locationInput.trim() ? data?.searchLocation : null}
                            dispatcher={dispatcher}
                            dispatcherItems={dispatcherItems}
                            onDispatcherChange={setDispatcher}
                            equipment={equipment}
                            equipmentItems={EQUIPMENT_ITEMS}
                            onEquipmentChange={setEquipment}
                            readyDate={readyDate}
                            onReadyDateChange={setReadyDate}
                            onClearAll={handleClearAll}
                        />
                    }
                    trucks={trucks}
                    loading={isLoading}
                    error={isError}
                    sort={sort}
                    onSort={setSort}
                    locationSortKey={searching ? DISTANCE_SORT_KEY : "location"}
                />

                {!isLoading && !isError && totalCount > 0 && (
                    <div className="relative mt-4 flex min-h-[40px] items-center justify-center">
                        <Pagination
                            currentPage={safePage}
                            totalPages={totalPages}
                            itemsPerPage={perPage}
                            totalItems={totalCount}
                            onPageChange={setPage}
                            itemsLabel="trucks"
                        />
                        <div className="absolute right-0 flex items-center gap-2">
                            <span className="whitespace-nowrap text-sm text-cm-p-text-secondary">Trucks per page</span>
                            <TruckListSelect items={PER_PAGE_ITEMS} value={perPage} onChange={setPerPage} width={88} />
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};
